import { useEffect, useRef, useState } from 'react';
import { AlertCircle, Camera, Copy, FolderClock, Loader, RefreshCw, RotateCw, ShieldCheck } from 'lucide-react';
import type { ModpackConfig, ModpackConfigEnv } from '../../types/modpackConfig';
import type { CreatePulsarJob, PulsarAction, PulsarBatch, PulsarConfig, PulsarServer } from '../../types/pulsar';
import { pulsarApi } from '../../services/pulsarApi';
import { pulsarJobActive, pulsarPathError, pulsarReady, pulsarWhen } from '../../utils/pulsar';
import { SectionHeader } from '../ui/SectionHeader';
import { PulsarJobHistory, PulsarReadOnlyNotice, PulsarSnapshots, PulsarStatus } from './PulsarDetails';
import { PulsarSettings } from './PulsarSettings';
import { PulsarFiles } from './PulsarFiles';

const message = (error: unknown) => error instanceof Error ? error.message : 'CDU Agent could not be reached.';

export function PulsarPanel({ config, env, canWrite, initialJobId }: { config: ModpackConfig; env: ModpackConfigEnv; canWrite: boolean; initialJobId?: string }) {
  if (env !== 'production') return <div className="alert alert-info"><ShieldCheck className="w-4 h-4 shrink-0" /><p>Templates and snapshots are available for production servers.</p></div>;
  return <PulsarWorkspace key={config.server_address} address={config.server_address} canWrite={canWrite} initialJobId={initialJobId} />;
}

function PulsarWorkspace({ address, canWrite, initialJobId }: { address: string; canWrite: boolean; initialJobId?: string }) {
  const [server, setServer] = useState<PulsarServer | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [lastSync, setLastSync] = useState(0);
  const [now, setNow] = useState(Date.now());
  const [reload, setReload] = useState(0);
  const [path, setPath] = useState('');
  const [scope, setScope] = useState<'server' | 'cluster'>('server');
  const [busy, setBusy] = useState<PulsarAction | null>(null);
  const [mutationError, setMutationError] = useState<string | null>(null);
  const [batch, setBatch] = useState<PulsarBatch | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(initialJobId ?? null);
  const [batchError, setBatchError] = useState<string | null>(null);
  const [batchSync, setBatchSync] = useState(0);
  const pending = useRef<{ fingerprint: string; request_id: string } | null>(null);
  const refresh = () => setReload(value => value + 1);

  useEffect(() => { if (initialJobId) setSelectedId(initialJobId); }, [initialJobId]);

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 5000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout>;
    const poll = async () => {
      try {
        const result = await pulsarApi.server(address, controller.signal);
        if (controller.signal.aborted) return;
        setServer(result); setLoadError(null); setLastSync(Date.now());
      } catch (error) { if (!controller.signal.aborted) setLoadError(message(error)); }
      finally { if (!controller.signal.aborted) timer = setTimeout(poll, 5000); }
    };
    void poll();
    return () => { controller.abort(); clearTimeout(timer); };
  }, [address, reload]);

  useEffect(() => {
    if (!selectedId) return;
    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout>;
    const poll = async () => {
      let active = true;
      try {
        const result = await pulsarApi.job(selectedId, controller.signal);
        if (controller.signal.aborted) return;
        setBatch(result); setBatchError(null); setBatchSync(Date.now());
        active = result.jobs.some(pulsarJobActive);
        if (!active) setReload(value => value + 1);
      } catch (error) { if (!controller.signal.aborted) setBatchError(message(error)); }
      finally { if (!controller.signal.aborted && active) timer = setTimeout(poll, 3000); }
    };
    void poll();
    return () => { controller.abort(); clearTimeout(timer); };
  }, [selectedId]);

  const ready = pulsarReady(server, lastSync, now, loadError);
  const active = server?.jobs.some(pulsarJobActive) || batch?.jobs.some(pulsarJobActive);
  const writable = canWrite && ready && !busy && !active;
  const copyPath = path.trim();
  const pathError = pulsarPathError(copyPath);
  const clusterMembers = server?.cluster_members ?? [];
  const hasCluster = server?.cluster_id !== null && server?.cluster_id !== undefined && clusterMembers.length > 1;
  const clusterReady = hasCluster && clusterMembers.every(member => member.available);
  const selectedMembers = scope === 'cluster' ? clusterMembers : server ? [server] : [];

  const create = async (action: PulsarAction, config?: PulsarConfig) => {
    if (!writable || !server) return;
    const copyCluster = action === 'copy_config' && scope === 'cluster';
    if (action === 'copy_config' && (pathError || copyCluster && !clusterReady)) return;
    const body: Omit<CreatePulsarJob, 'request_id'> = {
      scope: copyCluster ? 'cluster' : 'server', target: copyCluster ? String(server.cluster_id) : address, action,
      ...(action === 'copy_config' ? { path: copyPath } : {}), ...(config ? { config } : {}),
    };
    const fingerprint = JSON.stringify(body);
    if (pending.current?.fingerprint !== fingerprint) pending.current = { fingerprint, request_id: crypto.randomUUID() };
    setBusy(action); setMutationError(null);
    try {
      const result = await pulsarApi.create({ ...body, request_id: pending.current.request_id });
      pending.current = null;
      setBatch(result); setSelectedId(result.id); setBatchError(null); setBatchSync(Date.now());
      refresh();
    } catch (error) { setMutationError(message(error)); refresh(); }
    finally { setBusy(null); }
  };

  const openJob = (id: string) => { if (id === selectedId) return; setBatch(null); setBatchError(null); setBatchSync(0); setSelectedId(id); };

  return <div className="space-y-5">
    <section className="subcard p-4">
      <SectionHeader title="Templates & snapshots" icon={FolderClock} description="CDU Agent uses this server’s existing templates, snapshot folders and Pulsar settings."
        actions={<button type="button" className="glass-button-sm" onClick={refresh} aria-label="Refresh templates and snapshots"><RefreshCw className="w-4 h-4" /></button>} />
      {!server && !loadError && <p role="status" className="flex gap-2 items-center text-sm text-[var(--text-muted)]"><Loader className="w-4 h-4 animate-spin" />Loading agent status…</p>}
      {loadError && <ErrorNotice text={`Status could not refresh: ${loadError}`} />}
      {server && !loadError && now - lastSync >= 20_000 && <p role="alert" className="text-sm text-amber-300">Status is out of date. Waiting for a fresh response before accepting changes.</p>}
      {lastSync > 0 && <p className="text-xs text-[var(--text-muted)]">Last refreshed: {pulsarWhen(lastSync / 1000)}</p>}
    </section>

    {server && <PulsarStatus server={server} ready={ready} />}
    {!canWrite && <PulsarReadOnlyNotice />}
    {mutationError && <ErrorNotice text={mutationError} />}

    {canWrite && <section className="subcard p-4 space-y-3" aria-label="Snapshot actions">
      <SectionHeader title="Server actions" icon={Camera} />
      <p className="text-sm text-[var(--text-secondary)]">Take a snapshot now, or reload settings after editing <span className="font-mono">config/pulsar.json</span> on this server.</p>
      <div className="flex flex-wrap gap-2"><button type="button" className="btn-primary" disabled={!writable} onClick={() => void create('snapshot')}>{busy === 'snapshot' ? <Loader className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}Take snapshot</button><button type="button" className="glass-button-secondary" disabled={!writable} onClick={() => void create('reload')}><RotateCw className="w-4 h-4" />Reload settings</button></div>
      {!!active && <p role="status" className="text-sm text-amber-300">An operation is in progress. Its status is shown below.</p>}
    </section>}

    {canWrite && <section className="subcard p-4 space-y-3" aria-label="Copy configs to templates">
      <SectionHeader title="Copy configs to templates" icon={Copy} description="Preserve a live file or directory for subsequent server startups." />
      <fieldset disabled={!!busy} className="space-y-3">
        <label className="block text-sm text-[var(--text-secondary)]">Live file or directory<input className="glass-input w-full mt-1 font-mono" placeholder="config/example.toml" value={path} onChange={event => setPath(event.target.value)} maxLength={1024} /></label>
        <label className="block text-sm text-[var(--text-secondary)]">Copy on<select aria-label="Copy on" className="glass-select w-full mt-1" value={scope} onChange={event => setScope(event.target.value as 'server' | 'cluster')}><option value="server">This server · {address}</option>{hasCluster && <option value="cluster">Entire cluster · {clusterMembers.length} servers</option>}</select></label>
        {path && pathError ? <p role="alert" className="text-sm text-amber-300">{pathError}</p> : copyPath && <p className="text-sm font-mono break-all text-[var(--text-primary)]">{copyPath} → templates/{copyPath}</p>}
        <p className="text-xs text-[var(--text-muted)]">Each selected server copies its own live files into its own templates directory. Existing template files at that path are overwritten. The templates are applied on the next startup.</p>
        <ul className="space-y-1 text-xs text-[var(--text-secondary)]" aria-label="Copy target servers">{selectedMembers.map(member => <li key={member.server_address} className="break-all">{member.server_address}{!member.available && <span className="text-amber-300"> · {member.reason || 'Unavailable'}</span>}</li>)}</ul>
        <button type="button" className="btn-primary" disabled={!writable || !!pathError || scope === 'cluster' && !clusterReady} onClick={() => void create('copy_config')}>{busy === 'copy_config' ? <Loader className="w-4 h-4 animate-spin" /> : <Copy className="w-4 h-4" />}Copy to templates{scope === 'cluster' ? ` on ${clusterMembers.length} servers` : ''}</button>
      </fieldset>
    </section>}

    {selectedId && <div className="space-y-2" aria-live="polite">
      {batchError && <ErrorNotice text={`Operation status could not refresh: ${batchError}`} />}
      {!batch && !batchError && <p role="status" className="text-sm text-[var(--text-muted)]">Loading operation…</p>}
      {batch && <PulsarJobHistory jobs={batch.jobs} title="Selected operation" />}
      {batch && batch.jobs.some(pulsarJobActive) && <p className="text-xs text-[var(--text-muted)]">{batchSync && now - batchSync >= 20_000 ? 'Waiting for fresh operation status. ' : ''}Closing this page does not stop the operation.</p>}
    </div>}

    {server && <PulsarSnapshots server={server} />}
    {server && <PulsarFiles address={address} report={server.report} ready={ready} />}
    {canWrite && server?.report?.config && <PulsarSettings key={JSON.stringify(server.report.config)} config={server.report.config} disabled={!writable} saving={busy === 'configure'} onSave={config => void create('configure', config)} />}
    {server && <PulsarJobHistory jobs={server.jobs} onOpen={openJob} />}
  </div>;
}

function ErrorNotice({ text }: { text: string }) {
  return <div role="alert" className="alert alert-danger"><AlertCircle className="w-4 h-4 shrink-0" /><p>{text}</p></div>;
}
